import { html, nothing, unsafeCSS, type CSSResultGroup, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LibElement } from '../lib-element';
import type { LibBreadcrumbItem } from '../breadcrumb-item/lib-breadcrumb-item';
import breadcrumbCss from './lib-breadcrumb.css';

@customElement('lib-breadcrumb')
export class LibBreadcrumb extends LibElement {
  protected override get hostFocusSelector(): string { return this.collapsed ? 'button' : ''; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(breadcrumbCss)];
  @property({ type: Number, attribute: 'max-items' }) maxItems: number | null = null;
  @property({ type: Boolean, attribute: 'hide-on-scroll' }) hideOnScroll = false;
  @property() label = 'Breadcrumb';
  @property({ attribute: 'show-all-label' }) showAllLabel = 'Show all levels';
  @state() private expanded = false;
  @state() private collapsed = false;
  @state() private scrollHidden = false;
  private lastScrollY = 0;
  private frame = 0;

  private readonly onScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      const y = window.scrollY;
      this.scrollHidden = this.hideOnScroll && y > this.lastScrollY && y > 48;
      this.lastScrollY = y;
      this.frame = 0;
    });
  };

  override connectedCallback() {
    super.connectedCallback();
    this.lastScrollY = window.scrollY;
    window.addEventListener('scroll', this.onScroll, { passive: true });
  }

  override disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    cancelAnimationFrame(this.frame);
    this.frame = 0;
    super.disconnectedCallback();
  }

  protected override updated(changed: PropertyValues) {
    if (changed.has('maxItems') || changed.has('expanded')) this.syncItems();
    if (changed.has('hideOnScroll') && !this.hideOnScroll) this.scrollHidden = false;
  }

  private readonly syncItems = () => {
    const items = Array.from(this.children).filter((child): child is LibBreadcrumbItem => child.tagName === 'LIB-BREADCRUMB-ITEM');
    const max = this.maxItems;
    this.collapsed = !this.expanded && max !== null && max >= 3 && items.length > max;
    for (const [index, item] of items.entries()) {
      item.slot = index === 0 ? 'first' : '';
      item.hidden = this.collapsed && index > 0 && index < items.length - (max! - 2);
      item.separator = index > 0;
      item.setAttribute('role', 'listitem');
    }
  };

  override render() {
    return html`<nav class="lib-breadcrumb ${this.scrollHidden ? 'lib-breadcrumb-hidden' : ''}" aria-label=${this.label}>
      <ol class="lib-breadcrumb-list" role="list">
        <slot name="first" @slotchange=${this.syncItems}></slot>
        ${this.collapsed ? html`<li class="lib-breadcrumb-item lib-breadcrumb-truncation">
          <lib-icon icon-url="./_content/WAGO.SolutionsPlatform.UILibrary.Core/wago-icons-svg/chevron-right.svg" aria-hidden="true"></lib-icon>
          <button type="button" class="lib-breadcrumb-link lib-breadcrumb-truncation-button"
              aria-label=${this.showAllLabel} @click=${() => { this.expanded = true; }}>…</button>
        </li>` : nothing}
        <slot @slotchange=${this.syncItems}></slot>
      </ol>
      <div class="lib-breadcrumb-trailing"><slot name="trailing"></slot></div>
    </nav>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-breadcrumb': LibBreadcrumb; }
}
